import React from "react";

/* ──────────────────────────────────────────────
   TYPES
────────────────────────────────────────────── */

export interface ProductsAttributeValue {
  id: number;
  name: string;
  hexCodes?: string[];
}

export interface ProductsAttributeGroup {
  id: number;
  name: string;
  values: ProductsAttributeValue[];
}

/* ──────────────────────────────────────────────
   ATTRIBUTE IDS (SAFE + STABLE)
────────────────────────────────────────────── */

export const GENERAL_COLOR_ATTRIBUTE_ID = 90001;

/* ──────────────────────────────────────────────
   PRODUCTS ATTRIBUTES
────────────────────────────────────────────── */

export const productColors: ProductsAttributeGroup[] = [
  {
    id: GENERAL_COLOR_ATTRIBUTE_ID,
    name: "Color",
    values: [

      /* ───── NEUTRALS ───── */
      { id: 9000110, name: "Off White",     hexCodes: ["#F8F8F0"] },
      { id: 9000111, name: "Pearl",         hexCodes: ["#EAE0C8"] },
      { id: 9000112, name: "Stone",         hexCodes: ["#B8B5A9"] },
      { id: 9000113, name: "Taupe",         hexCodes: ["#483C32"] },
      { id: 9000114, name: "Sand",          hexCodes: ["#C2B280"] },
      { id: 9000115, name: "Khaki",         hexCodes: ["#C3B091"] },
      { id: 9000116, name: "Camel",         hexCodes: ["#C19A6B"] },
      { id: 9000117, name: "Mocha",         hexCodes: ["#967969"] },
      { id: 9000118, name: "Chocolate",     hexCodes: ["#7B3F00"] },
      { id: 9000119, name: "Espresso",      hexCodes: ["#4B3621"] },

      /* ───── BLACK & WHITE / GRAYSCALE ───── */
      { id: 9001101, name: "Black",         hexCodes: ["#000000"] },
      { id: 9001102, name: "Licorice",      hexCodes: ["#0C0C0C"] },
      { id: 9001103, name: "Jet Black",     hexCodes: ["#191919"] },
      { id: 9001104, name: "Graphite",      hexCodes: ["#262626"] },
      { id: 9001105, name: "Charcoal",      hexCodes: ["#323232"] },
      { id: 9001106, name: "Dark Gray",     hexCodes: ["#4C4C4C"] },
      { id: 9001107, name: "Ash Gray",      hexCodes: ["#666666"] },
      { id: 9001108, name: "Medium Gray",   hexCodes: ["#7F7F7F"] },
      { id: 9001109, name: "Silver Gray",   hexCodes: ["#999999"] },
      { id: 9001110, name: "Light Gray",    hexCodes: ["#B2B2B2"] },
      { id: 9001111, name: "Gainsboro",     hexCodes: ["#CCCCCC"] },
      { id: 9001112, name: "Smoke",         hexCodes: ["#E5E5E5"] },
      { id: 9001113, name: "White",         hexCodes: ["#FFFFFF"] },

      /* ───── REDS ───── */
      { id: 9000401, name: "Pure Red",       hexCodes: ["#FF0000"] },
      { id: 9000402, name: "Vivid Red",      hexCodes: ["#E60000"] },
      { id: 9000403, name: "True Red",       hexCodes: ["#CF0101"] },
      { id: 9000404, name: "Bold Red",       hexCodes: ["#C00000"] },
      { id: 9000405, name: "Dark Red",       hexCodes: ["#A60202"] },
      { id: 9000416, name: "Light Coral",    hexCodes: ["#FF8B8E"] },
      { id: 9000417, name: "Neon Red",       hexCodes: ["#FF3D41"] },
      { id: 9000418, name: "Firetruck Red",  hexCodes: ["#DC0005"] },
      { id: 9000419, name: "Deep Red",       hexCodes: ["#C80004"] },
      { id: 9000420, name: "Maroon",         hexCodes: ["#B40000"] },
      { id: 9000421, name: "Burgundy",       hexCodes: ["#690000"] },
      { id: 9000422, name: "Flame Red",      hexCodes: ["#E31919"] },
      { id: 9000423, name: "Vermilion",      hexCodes: ["#EA2300"] },
      { id: 9000424, name: "Ruby Red",       hexCodes: ["#DD1212"] },
      { id: 9000425, name: "Bright Red",     hexCodes: ["#F02525"] },
      { id: 9000426, name: "Hot Red",        hexCodes: ["#EA0A0A"] },
      { id: 9000427, name: "Candy Red",      hexCodes: ["#DD1717"] },
      { id: 9000428, name: "Tomato Red",     hexCodes: ["#FB3310"] },
      { id: 9000429, name: "Rose Red",       hexCodes: ["#F40D30"] },
      { id: 9000430, name: "Cardinal",       hexCodes: ["#C70E0E"] },
      { id: 9000431, name: "Raspberry",      hexCodes: ["#D6204E"] },
      { id: 9000406, name: "Crimson",        hexCodes: ["#DC143C"] },
      { id: 9000407, name: "Scarlet",        hexCodes: ["#FF2400"] },
      { id: 9000408, name: "Cherry Red",     hexCodes: ["#D2042D"] },
      { id: 9000409, name: "Wine",           hexCodes: ["#722F37"] },
      { id: 9000410, name: "Coral",          hexCodes: ["#FF7F50"] },
      { id: 9000411, name: "Salmon",         hexCodes: ["#FA8072"] },
      { id: 9000412, name: "Blush Pink",     hexCodes: ["#F4C2C2"] },
      { id: 9000413, name: "Hot Pink",       hexCodes: ["#FF69B4"] },
      { id: 9000414, name: "Fuchsia",        hexCodes: ["#FF00FF"] },
      { id: 9000415, name: "Magenta",        hexCodes: ["#D1007A"] },

      /* ───── PINKS ───── */
      { id: 9001201, name: "Electric Pink",  hexCodes: ["#FB00BE"] },
      { id: 9001202, name: "Neon Pink",      hexCodes: ["#FF0074"] },
      { id: 9001203, name: "Bright Pink",    hexCodes: ["#FF00CE"] },
      { id: 9001204, name: "Deep Pink",      hexCodes: ["#FF07A9"] },
      { id: 9001205, name: "Cerise",         hexCodes: ["#EE006C"] },

      /* ───── BERRY ───── */
      { id: 9001301, name: "Plum Berry",     hexCodes: ["#990F4B"] },
      { id: 9001302, name: "Mulberry",       hexCodes: ["#AD3D5E"] },
      { id: 9001303, name: "Dusty Rose",     hexCodes: ["#C05F72"] },
      { id: 9001304, name: "Mauve Pink",     hexCodes: ["#D27F87"] },
      { id: 9001305, name: "Blush Berry",    hexCodes: ["#E39E9C"] },

      /* ───── ORANGES & YELLOWS ───── */
      { id: 9000505, name: "Peach",          hexCodes: ["#FFE5B4"] },
      { id: 9000506, name: "Apricot",        hexCodes: ["#FBCEB1"] },
      { id: 9000507, name: "Tangerine",      hexCodes: ["#F28500"] },
      { id: 9000508, name: "Burnt Orange",   hexCodes: ["#CC5500"] },
      { id: 9000509, name: "Rust",           hexCodes: ["#B7410E"] },
      { id: 9000510, name: "Golden Yellow",  hexCodes: ["#FFDF00"] },
      { id: 9000511, name: "Lemon",          hexCodes: ["#FFF44F"] },
      { id: 9000512, name: "Canary Yellow",  hexCodes: ["#FFEF00"] },
      { id: 9000513, name: "Sunflower",      hexCodes: ["#FFC512"] },

      /* ───── ORANGES (EXTENDED) ───── */
      { id: 9001401, name: "Burnt Sienna",   hexCodes: ["#C65102"] },
      { id: 9001402, name: "Amber Orange",   hexCodes: ["#D56602"] },
      { id: 9001403, name: "Dark Amber",     hexCodes: ["#E37B01"] },
      { id: 9001404, name: "Deep Amber",     hexCodes: ["#F19001"] },
      { id: 9001405, name: "Orange",         hexCodes: ["#FFA500"] },
      { id: 9001406, name: "Pale Orange",    hexCodes: ["#FDCC99"] },
      { id: 9001407, name: "Warm Orange",    hexCodes: ["#FB9932"] },
      { id: 9001408, name: "Pumpkin",        hexCodes: ["#FA8000"] },
      { id: 9001409, name: "Bronze Orange",  hexCodes: ["#C86600"] },
      { id: 9001410, name: "Dark Pumpkin",   hexCodes: ["#7D4000"] },

      /* ───── YELLOWS (EXTENDED) ───── */
      { id: 9001501, name: "Pale Yellow",    hexCodes: ["#FCF5B2"] },
      { id: 9001502, name: "Soft Yellow",    hexCodes: ["#FBEE7F"] },
      { id: 9001503, name: "Bright Yellow",  hexCodes: ["#F9E432"] },
      { id: 9001504, name: "Pure Yellow",    hexCodes: ["#F8DE00"] },
      { id: 9001505, name: "Gold Yellow",    hexCodes: ["#DFC700"] },
      { id: 9001506, name: "Cream Yellow",   hexCodes: ["#FFFBA0"] },
      { id: 9001507, name: "Butter",         hexCodes: ["#FFEC8E"] },
      { id: 9001508, name: "Straw",          hexCodes: ["#FEE283"] },
      { id: 9001509, name: "Honey",          hexCodes: ["#F6D484"] },
      { id: 9001510, name: "Warm Honey",     hexCodes: ["#EFC576"] },

      /* ───── GREENS ───── */
      { id: 9000305, name: "Emerald",        hexCodes: ["#50C878"] },
      { id: 9000306, name: "Jade",           hexCodes: ["#00A86B"] },
      { id: 9000307, name: "Sage",           hexCodes: ["#9CAF88"] },
      { id: 9000308, name: "Moss Green",     hexCodes: ["#8A9A5B"] },
      { id: 9000309, name: "Army Green",     hexCodes: ["#4B5320"] },
      { id: 9000310, name: "Pistachio",      hexCodes: ["#93C572"] },
      { id: 9000311, name: "Sea Green",      hexCodes: ["#2E8B57"] },
      { id: 9000312, name: "Kelly Green",    hexCodes: ["#4CBB17"] },
      { id: 9000313, name: "Neon Green",     hexCodes: ["#39FF14"] },
      { id: 9000314, name: "Dark Green",     hexCodes: ["#006400"] },

      /* ───── GREENS (EXTENDED) ───── */
      { id: 9001601, name: "Mint",           hexCodes: ["#CDF5D2"] },
      { id: 9001602, name: "Spring Green",   hexCodes: ["#69E279"] },
      { id: 9001603, name: "Vivid Green",    hexCodes: ["#04A519"] },
      { id: 9001604, name: "Forest Green",   hexCodes: ["#049016"] },
      { id: 9001605, name: "Deep Forest",    hexCodes: ["#013E09"] },
      { id: 9001606, name: "Lime Green",     hexCodes: ["#00FF00"] },
      { id: 9001607, name: "Bright Lime",    hexCodes: ["#05C800"] },
      { id: 9001608, name: "Grass Green",    hexCodes: ["#069300"] },
      { id: 9001609, name: "Fern",           hexCodes: ["#056200"] },
      { id: 9001610, name: "Pine",           hexCodes: ["#033500"] },
      { id: 9001611, name: "Laurel",         hexCodes: ["#AAD688"] },
      { id: 9001612, name: "Artichoke",      hexCodes: ["#98C377"] },
      { id: 9001613, name: "Herb Green",     hexCodes: ["#8BBD78"] },
      { id: 9001614, name: "Leaf Green",     hexCodes: ["#5EA758"] },
      { id: 9001615, name: "Dark Sage",      hexCodes: ["#47894B"] },
      { id: 9001616, name: "Neon Lime",      hexCodes: ["#76FF7B"] },
      { id: 9001617, name: "Lime Punch",     hexCodes: ["#A1FF7B"] },
      { id: 9001618, name: "Green Yellow",   hexCodes: ["#C4FF7B"] },
      { id: 9001619, name: "Yellow Lime",    hexCodes: ["#E3FF7A"] },
      { id: 9001620, name: "Lemon Lime",     hexCodes: ["#FFFE7A"] },

      /* ───── BLUES ───── */
      { id: 9000206, name: "Baby Blue",      hexCodes: ["#89CFF0"] },
      { id: 9000207, name: "Powder Blue",    hexCodes: ["#B0E0E6"] },
      { id: 9000208, name: "Steel Blue",     hexCodes: ["#4682B4"] },
      { id: 9000209, name: "Denim Blue",     hexCodes: ["#1560BD"] },
      { id: 9000210, name: "Cobalt Blue",    hexCodes: ["#0047AB"] },
      { id: 9000211, name: "Sapphire",       hexCodes: ["#0F52BA"] },
      { id: 9000212, name: "Aqua",           hexCodes: ["#00FFFF"] },
      { id: 9000213, name: "Cyan",           hexCodes: ["#00BCD4"] },
      { id: 9000214, name: "Petrol Blue",    hexCodes: ["#005F6A"] },
      { id: 9000215, name: "Ice Blue",       hexCodes: ["#D6F5FF"] },

      /* ───── BLUES (EXTENDED) ───── */
      { id: 9001701, name: "Midnight Blue",  hexCodes: ["#040273"] },
      { id: 9001702, name: "Navy",           hexCodes: ["#022477"] },
      { id: 9001703, name: "Royal Navy",     hexCodes: ["#0E3879"] },
      { id: 9001704, name: "Dark Blue",      hexCodes: ["#1F4A7C"] },
      { id: 9001705, name: "Ocean Blue",     hexCodes: ["#315B7D"] },
      { id: 9001706, name: "Cornflower",     hexCodes: ["#5170D7"] },
      { id: 9001707, name: "Periwinkle",     hexCodes: ["#6E8CDC"] },
      { id: 9001708, name: "Muted Blue",     hexCodes: ["#8DA6DF"] },
      { id: 9001709, name: "Sky Blue",       hexCodes: ["#1A84B8"] },
      { id: 9001710, name: "Azure",          hexCodes: ["#1A8CB8"] },
      { id: 9001711, name: "Cerulean",       hexCodes: ["#1A94B8"] },
      { id: 9001712, name: "Peacock Blue",   hexCodes: ["#1A9CB8"] },
      { id: 9001713, name: "Lagoon Blue",    hexCodes: ["#1AA4B8"] },

      /* ───── BLUE GRAY ───── */
      { id: 9001801, name: "Slate Blue",     hexCodes: ["#758DA3"] },
      { id: 9001802, name: "Storm Blue",     hexCodes: ["#859CB2"] },
      { id: 9001803, name: "Dusty Blue",     hexCodes: ["#95ABC2"] },
      { id: 9001804, name: "Glacier Blue",   hexCodes: ["#A6BAD2"] },
      { id: 9001805, name: "Pale Steel",     hexCodes: ["#B7C9E2"] },

      /* ───── DENIM ───── */
      { id: 9001901, name: "Dark Denim",     hexCodes: ["#071F35"] },
      { id: 9001902, name: "Indigo Denim",   hexCodes: ["#24496B"] },
      { id: 9001903, name: "Medium Denim",   hexCodes: ["#446D92"] },
      { id: 9001904, name: "Faded Denim",    hexCodes: ["#98B1C8"] },
      { id: 9001905, name: "Washed Denim",   hexCodes: ["#F7FAFE"] },

      /* ───── AQUA BLUE ───── */
      { id: 9002001, name: "Soft Aqua",      hexCodes: ["#90C0E0"] },
      { id: 9002002, name: "Light Aqua",     hexCodes: ["#80B8D8"] },
      { id: 9002003, name: "Vivid Aqua",     hexCodes: ["#3890C8"] },
      { id: 9002004, name: "Bright Aqua",    hexCodes: ["#60A8D0"] },
      { id: 9002005, name: "Pale Aqua",      hexCodes: ["#98C8E0"] },

      /* ───── CYAN ───── */
      { id: 9002101, name: "Electric Cyan",  hexCodes: ["#00FFFF"] },
      { id: 9002102, name: "Bright Cyan",    hexCodes: ["#00E1E1"] },
      { id: 9002103, name: "Medium Cyan",    hexCodes: ["#00C3C3"] },
      { id: 9002104, name: "Deep Cyan",      hexCodes: ["#00A7A7"] },
      { id: 9002105, name: "Dark Teal",      hexCodes: ["#008B8B"] },

      /* ───── PURPLES ───── */
      { id: 9000604, name: "Violet",         hexCodes: ["#8F00FF"] },
      { id: 9000605, name: "Lilac",          hexCodes: ["#C8A2C8"] },
      { id: 9000606, name: "Mauve",          hexCodes: ["#E0B0FF"] },
      { id: 9000607, name: "Orchid",         hexCodes: ["#DA70D6"] },
      { id: 9000608, name: "Eggplant",       hexCodes: ["#614051"] },
      { id: 9000609, name: "Deep Purple",    hexCodes: ["#512DA8"] },

      /* ───── PURPLES (EXTENDED) ───── */
      { id: 9002201, name: "Electric Purple", hexCodes: ["#B700FF"] },
      { id: 9002202, name: "Vivid Purple",   hexCodes: ["#9A00D6"] },
      { id: 9002203, name: "Grape",          hexCodes: ["#7900A8"] },
      { id: 9002204, name: "Dark Grape",     hexCodes: ["#600085"] },
      { id: 9002205, name: "Blackberry",     hexCodes: ["#300042"] },

      /* ───── VIOLET / LAVENDER ───── */
      { id: 9002301, name: "Soft Violet",    hexCodes: ["#D6B4FC"] },
      { id: 9002302, name: "Wisteria",       hexCodes: ["#E0BCF5"] },
      { id: 9002303, name: "Lavender",       hexCodes: ["#EAC3EE"] },
      { id: 9002304, name: "Pale Lavender",  hexCodes: ["#F4CAE7"] },
      { id: 9002305, name: "Blush Lavender", hexCodes: ["#FFD1DF"] },

      /* ───── INDIGO ───── */
      { id: 9002401, name: "Midnight Indigo", hexCodes: ["#191970"] },
      { id: 9002402, name: "Royal Indigo",   hexCodes: ["#002395"] },
      { id: 9002403, name: "Dark Indigo",    hexCodes: ["#00416A"] },
      { id: 9002404, name: "Indigo Blue",    hexCodes: ["#1560BD"] },
      { id: 9002405, name: "Soft Indigo",    hexCodes: ["#5D76CB"] },

      /* ───── TRUE COLORS / HERITAGE ───── */
      { id: 9002501, name: "Deep Crimson",   hexCodes: ["#830000"] },
      { id: 9002502, name: "Dark Sienna",    hexCodes: ["#8B4D00"] },
      { id: 9002503, name: "Olive Drab",     hexCodes: ["#666700"] },
      { id: 9002504, name: "Hunter Green",   hexCodes: ["#00510E"] },
      { id: 9002505, name: "Midnight Navy",  hexCodes: ["#000440"] },

      /* ───── BROWNS ───── */
      { id: 9000901, name: "Tan",            hexCodes: ["#D2B48C"] },
      { id: 9000902, name: "Chestnut",       hexCodes: ["#954535"] },
      { id: 9000903, name: "Walnut",         hexCodes: ["#5D432C"] },
      { id: 9000904, name: "Mahogany",       hexCodes: ["#C04000"] },
      { id: 9000905, name: "Cocoa",          hexCodes: ["#6F4E37"] },

      /* ───── BROWNS (EXTENDED) ───── */
      { id: 9002601, name: "Sandy Brown",    hexCodes: ["#FA9755"] },
      { id: 9002602, name: "Warm Brown",     hexCodes: ["#C77844"] },
      { id: 9002603, name: "Hazel",          hexCodes: ["#87522E"] },
      { id: 9002604, name: "Umber",          hexCodes: ["#7B4B2A"] },
      { id: 9002605, name: "Toffee",         hexCodes: ["#613B21"] },
      { id: 9002606, name: "Dark Toffee",    hexCodes: ["#553311"] },
      { id: 9002607, name: "Auburn Brown",   hexCodes: ["#664433"] },
      { id: 9002608, name: "Seal Brown",     hexCodes: ["#332211"] },
      { id: 9002609, name: "Caramel",        hexCodes: ["#996633"] },
      { id: 9002610, name: "Warm Caramel",   hexCodes: ["#CCAA66"] },
      { id: 9002611, name: "Ginger",         hexCodes: ["#CC8833"] },
      { id: 9002612, name: "Cognac",         hexCodes: ["#D08C42"] },
      { id: 9002613, name: "Whiskey",        hexCodes: ["#D4914F"] },
      { id: 9002614, name: "Teak",           hexCodes: ["#D9955B"] },
      { id: 9002615, name: "Butterscotch",   hexCodes: ["#DD9966"] },

      /* ───── CHESTNUTS ───── */
      { id: 9002701, name: "Dark Chestnut",  hexCodes: ["#742802"] },
      { id: 9002702, name: "Russet",         hexCodes: ["#8F4934"] },
      { id: 9002703, name: "Clay",           hexCodes: ["#A96A5C"] },
      { id: 9002704, name: "Rosy Brown",     hexCodes: ["#C38B85"] },
      { id: 9002705, name: "Pale Chestnut",  hexCodes: ["#DDADAF"] },

      /* ───── METALLICS ───── */
      { id: 9000701, name: "Gold",           hexCodes: ["#D4AF37"] },
      { id: 9000702, name: "Rose Gold",      hexCodes: ["#B76E79"] },
      { id: 9000703, name: "Copper",         hexCodes: ["#B87333"] },
      { id: 9000704, name: "Bronze",         hexCodes: ["#CD7F32"] },
      { id: 9000705, name: "Gunmetal",       hexCodes: ["#2A3439"] },
      { id: 9000706, name: "Titanium",       hexCodes: ["#878681"] },
      { id: 9000707, name: "Platinum",       hexCodes: ["#E5E4E2"] },
      { id: 9000708, name: "Chrome",         hexCodes: ["#D7D7D7"] },

      /* ───── PASTELS ───── */
      { id: 9001001, name: "Pastel Blue",    hexCodes: ["#AEC6CF"] },
      { id: 9001002, name: "Pastel Green",   hexCodes: ["#77DD77"] },
      { id: 9001003, name: "Pastel Pink",    hexCodes: ["#FFD1DC"] },
      { id: 9001004, name: "Pastel Yellow",  hexCodes: ["#FDFD96"] },
      { id: 9001005, name: "Pastel Purple",  hexCodes: ["#C3B1E1"] },

      /* ───── SPECIAL / MIXED ───── */
      { id: 9000801, name: "Multicolor" },
      { id: 9000802, name: "Transparent" },
      { id: 9000803, name: "Matte Black",    hexCodes: ["#1C1C1C"] },
      { id: 9000804, name: "Glossy White",   hexCodes: ["#FAFAFA"] },

      /* ───── SPECIAL EFFECTS ───── */
      { id: 9000805, name: "Rainbow" },
      { id: 9000806, name: "Iridescent" },
      { id: 9000807, name: "Holographic" },
      { id: 9000808, name: "Metallic" },
      { id: 9000809, name: "Glow in the Dark" },
      { id: 9000810, name: "Clear",          hexCodes: ["#FFFFFF"] },
    ],
  },
];